import { IDatabaseColumnModel } from "@kuika/kuika-cl-model";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react";
import {
  IManagedDbBatchRequest,
  IManagedDbForeignKey,
  IManagedDbInboundForeignKey,
  IManagedDbRow,
  ManagedDbFilterOperator
} from "../../services/table-editor-service";
import useLoadingPresentation, { type LoadingPresentation } from "../../shared/hooks/useLoadingPresentation";
import GenerateWithAiSvg from "../../svgs/generate-with-ai.svg";
import { getEditorGlyph, IEditorGlyph } from "../design-tools/data-diagram/editor-switcher/editor-glyph";
import { EditorGlyphIcon } from "../design-tools/data-diagram/editor-switcher/editor-glyph-icon";
import KuikaBadge from "../ui-kit/kuika-badge/kuika-badge";
import KuikaButton, {
  KUIKA_BUTTON_ERROR_HOLD_MS,
  KUIKA_BUTTON_SUCCESS_HOLD_MS,
  type KuikaButtonStatus
} from "../ui-kit/kuika-button/kuika-button";
import { type KuikaDropdownMenuItemType } from "../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaEntitySwitcher, { KuikaEntitySwitcherItem } from "../ui-kit/kuika-entity-switcher/kuika-entity-switcher";
import KuikaLoader from "../ui-kit/kuika-loader/kuika-loader";
import KuikaNumber from "../ui-kit/kuika-number/kuika-number";
import { KuikaEditorIdentity } from "../ui-kit/kuika-page-header/kuika-editor-title";
import KuikaPageHeader from "../ui-kit/kuika-page-header/kuika-page-header";
import KuikaSelectbox from "../ui-kit/kuika-selectbox/kuika-selectbox";
import { KuikaSkeletonRegion, KuikaSkeletonTable } from "../ui-kit/kuika-skeleton/kuika-skeleton";
import KuikaSplitButton from "../ui-kit/kuika-split-button/kuika-split-button";
import KuikaSvg from "../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../ui-kit/kuika-tooltip/kuika-tooltip";
import AiGenerateRowsModal from "./ai-generate-rows-modal";
import BufferedExportConfirmModal from "./buffered-export-confirm-modal";
import { IColumnFilterEntry, IColumnFilterMap, isValuelessOperator } from "./column-filters";
import DiscardConfirmPopover from "./discard-confirm-popover";
import EditableDataGrid, { IGridSortState, ISelectionModifiers } from "./editable-data-grid";
import { useTableEditorAi } from "./hooks/use-table-editor-ai";
import { useTableEditorExport } from "./hooks/use-table-editor-export";
import { buildBatchRequestFromPending, useTableEditorMutations } from "./hooks/use-table-editor-mutations";
import { useTableRowsQuery } from "./hooks/use-table-rows-query";
import ImportRowsModal, { IImportSummary } from "./import-rows-modal";
import { IPendingChanges, IPendingRowInsert, rowPkStringFromRow, RowPkString } from "./pending-changes-reducer";
import RowContextMenu, { IRowContextMenuOption } from "./row-context-menu";
import SqlPreviewModal from "./sql-preview-modal";
import TableEditorAlert, { ITableEditorAlertAction } from "./table-editor-alert";
import { TableEditorSqlDialect } from "./table-editor-dialect";
import { buildTableEditorErrorAlert, createTableEditorAlert, ITableEditorAlert } from "./table-editor-errors";
import "./table-editor.scss";
import {
  ColumnsIcon,
  ExportRowsIcon,
  ImportRowsIcon,
  RedoIcon,
  ReloadRowsIcon,
  UndoIcon
} from "./toolbar-action-icons.svg";
import { IColumnWidthMap, IPersistedView, IViewScope, readPersistedView, writePersistedView } from "./view-storage";

/** A table the row browser can open in place: schema-qualified name, keyed as the tree keys it. */
export interface ITableRowsSwitcherItem extends KuikaEntitySwitcherItem {
  schema: string;
  tableName: string;
}

export interface IInitialFilter {
  column: string;
  value: string;
  // Optional — FK "go to" navigation passes `eq` for exact PK match.
  // Older callers that omit this fall back to `eq` (was previously
  // hard-coded to `contains` server-side, which silently widened FK
  // jumps to substring matches).
  operator?: ManagedDbFilterOperator;
}

// FK navigation target — table + optional schema. Named so the
// onNavigateToTable signature doesn't carry an inline object shape
// (CLAUDE.md bans those) and so callers compose cleanly.
export interface ITableNavigationTarget {
  name: string;
  schema?: string;
}

/** What the rows hold that the host has not saved yet — enough for its guard and its tab badge. */
export interface ITableRowsPendingSummary {
  /** Edits that would reach the database; a blank draft row is not one. */
  isDirty: boolean;
  dirtyCount: number;
  isSaving: boolean;
}

/**
 * The row browser as the table editor's Data tab. The host's bar already names the table and is
 * the way out, so the editor draws no bar of its own — only a strip with its counts, tools and
 * commit — and tells the host what is pending, because the host's close guard has to cover rows.
 */
export interface ITableRowsEditorEmbedding {
  /** The Data tab is the one on screen. A hidden pane stays mounted and leaves ⌘S / ⌘Z alone. */
  isActive: boolean;
  onPendingChange: (summary: ITableRowsPendingSummary) => void;
  /** Shown first in the banner stack — the host's word on what the rows reflect. */
  notice?: React.ReactNode;
}

/** What the host can do to the rows from its own controls ("Save & close" in its close guard). */
export interface ITableRowsEditorHandle {
  /** True when nothing is left to save; false when the save did not go through (the alert says why). */
  saveAll: () => Promise<boolean>;
  discardAll: () => void;
}

export interface ITableRowsEditorProps {
  tableName: string;
  tableSchema?: string;
  sqlDialect?: TableEditorSqlDialect;
  // Identifier for the active datasource/connection. Scopes persisted
  // view state (filters, sort, page size, column widths, hidden cols)
  // so two projects browsing the same table name don't share a view.
  // Optional for back-compat — when absent, the scope collapses to a
  // shared bucket, same behavior as before.
  datasourceId?: string;
  /** The datasource as the designer names it ("Managed DB") — the bar's glyph says it on hover. */
  datasourceName?: string;
  onClose?: () => void;
  /**
   * The other tables of this datasource, and what to do when one is picked. The pick runs
   * through this editor's own discard guard, which opens AT the click, so the switcher hands
   * back where its trigger sat.
   */
  switcherItems?: ITableRowsSwitcherItem[];
  onSwitchTable?: (item: ITableRowsSwitcherItem) => void;
  // FK "Go to referenced row" navigation. The canvas wires this through
  // setBrowsingRowsTable so the editor re-mounts at the referenced table
  // with the FK target PK pre-filtered.
  onNavigateToTable?: (table: ITableNavigationTarget, initialFilter: IInitialFilter) => void;
  initialFilter?: IInitialFilter;
  /** Set when the table editor hosts this as its Data tab; absent, it is the standalone editor. */
  embedded?: ITableRowsEditorEmbedding;
}

const DEFAULT_PAGE_SIZE: number = 50;
const PAGE_SIZE_OPTIONS = [
  { value: 25, label: "25 / page" },
  { value: 50, label: "50 / page" },
  { value: 100, label: "100 / page" },
  { value: 200, label: "200 / page" }
];

// Right-click context-menu target. Discriminated by `kind` so the menu
// builder branches between persisted rows (PK known) and pending inserts
// (PK not yet stamped — key by tmp clientId). `columnName` (optional)
// carries the cell the user actually right-clicked on, which the row
// menu uses to add a cell-scoped "Set null" entry. Absent when the
// click landed on the row gutter / outside any data cell.
type ContextMenuTarget =
  | { kind: "row"; x: number; y: number; pk: RowPkString; columnName?: string }
  | { kind: "insert"; x: number; y: number; clientId: string; columnName?: string };

// Click-anchored popup position. Used by the column-picker dropdown
// (and shape-equivalent to the export dropdown's IExportAnchor inside
// Threshold below which the Discard button skips the confirm popover.
// One or two edits are cheap to recreate and ⌘Z still resurrects them
// inside the active session; three is the point where re-typing starts
// to sting. Mirror the Linear / Notion "small undo, no confirm; large
// undo, confirm" pattern.
const DISCARD_CONFIRM_THRESHOLD: number = 3;

// use-table-editor-export). Named here to avoid an inline { x; y }
// type parameter, per CLAUDE.md.
/** Counts read the same on every machine locale (a Turkish or German system would group differently). */
const EN_US_COUNT: Intl.NumberFormat = new Intl.NumberFormat("en-US");

interface IMenuAnchor {
  x: number;
  y: number;
}

// Compact preview for a cell value inside a context-menu label. Long
// strings truncate so the menu doesn't grow past the viewport on a
// row of essay-length comments; null/undefined render as the SQL
// keyword the rest of the editor uses.
const previewCellValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return "null";
  }
  const s: string = typeof value === "string" ? value : String(value);
  if (s.length <= 24) {
    return s;
  }
  return s.slice(0, 22) + "…";
};

// Build one submenu item per editable column on the right-clicked row.
// Picking a column dispatches editCell for every selected row with the
// source row's value. PK / identity / auto-increment columns are
// filtered — the SQL builder rejects updates to those, and the cells
// render read-only, so offering them in the menu would just confuse.
const buildFillColumnOptions = (
  sourceRow: IManagedDbRow,
  columns: IDatabaseColumnModel[],
  selectedRows: IManagedDbRow[],
  primaryKeyColumns: string[],
  editCell: (pk: RowPkString, column: string, value: unknown, originalValue: unknown) => void
): IRowContextMenuOption[] => {
  const editableColumns: IDatabaseColumnModel[] = columns.filter(
    (c: IDatabaseColumnModel): boolean => !(c.isPk === true || c.isIdentity === true || c.isAutoIncrement === true)
  );
  if (editableColumns.length === 0) {
    return [{ type: "item", label: "No editable columns", disabled: true, onClick: (): void => undefined }];
  }
  return editableColumns.map((col: IDatabaseColumnModel): IRowContextMenuOption => {
    const sourceValue: unknown = sourceRow[col.name];
    return {
      type: "item",
      label: `${col.name} = ${previewCellValue(sourceValue)}`,
      onClick: (): void => {
        selectedRows.forEach((target: IManagedDbRow): void => {
          // editCell takes the row's CURRENT value as `originalValue`
          // for the optimistic-concurrency snapshot. The reducer only
          // captures it on first-touch, so subsequent fills against
          // the same (pk, column) keep the original constant — what
          // the user actually saw before the bulk edit.
          const targetPk: RowPkString = rowPkStringFromRow(target, primaryKeyColumns);
          editCell(targetPk, col.name, sourceValue, target[col.name]);
        });
      }
    };
  });
};

// "Empty" for the AI-fill gate: null, undefined, or empty string. Whitespace
// strings count as filled — leading-space content is unusual but valid.
const isAiEmptyValue = (value: unknown): boolean => value === null || value === undefined || value === "";

// Types AI can produce meaningful values for. Mirrors the server-side
// `IsAiFillableType` in DatasourceService.TableEditor.Ai.cs — must
// stay in sync or the client offers "Fill with AI" on rows whose only
// empty cells the server then strips, surfacing as a confusing
// "warning: columns skipped" toast after the request.
//
// Excluded:
//   - Binary blobs (Image, Signature, FileContent, ByteArray,
//     VarBinary, Binary, Bytea) — the LLM would hallucinate base64
//     that decodes to garbage, or refuse outright.
//   - Object — Kuika's catch-all type has no canonical shape.
//   - Location — wire format is JSON {latitude, longitude}; round-
//     tripping that through freeform JSON is fragile and the model
//     doesn't know real geocoded points for the user's domain.
const COMPLEX_NON_FILLABLE_TYPES: ReadonlySet<string> = new Set<string>([
  "Image",
  "Signature",
  "FileContent",
  "ByteArray",
  "VarBinary",
  "Binary",
  "Bytea",
  "Object",
  "Location"
]);

// True when the column is something AI can propose a value for. Mirrors
// the server-side SelectFillableColumns: identity / auto-increment are
// DB-assigned, Guid PKs are stamped server-side after the model returns,
// and complex types (blobs / Object / Location) are deliberately
// skipped by both sides because the LLM can't produce useful values
// for them.
const isAiFillableColumn = (col: IDatabaseColumnModel): boolean => {
  if (col.isIdentity === true || col.isAutoIncrement === true) {
    return false;
  }
  if (col.isPk === true && col.kuikaDataType === "Guid") {
    return false;
  }
  if (typeof col.kuikaDataType === "string" && COMPLEX_NON_FILLABLE_TYPES.has(col.kuikaDataType)) {
    return false;
  }
  return true;
};

// True when at least one AI-fillable column on the row is empty, taking
// pending updates into account so a row the user has already filled with
// pending values is treated as filled even before save.
const rowHasFillableEmpties = (
  row: IManagedDbRow,
  pk: RowPkString,
  columns: IDatabaseColumnModel[],
  pending: IPendingChanges
): boolean => {
  const pendingUpdate = pending.updates.get(pk);
  return columns.some((c: IDatabaseColumnModel): boolean => {
    if (!isAiFillableColumn(c)) {
      return false;
    }
    let effective: unknown;
    if (pendingUpdate && Object.prototype.hasOwnProperty.call(pendingUpdate.changes, c.name)) {
      effective = pendingUpdate.changes[c.name];
    } else {
      effective = row[c.name];
    }
    return isAiEmptyValue(effective);
  });
};

const insertHasFillableEmpties = (insert: IPendingRowInsert, columns: IDatabaseColumnModel[]): boolean => {
  return columns.some((c: IDatabaseColumnModel): boolean => {
    if (!isAiFillableColumn(c)) {
      return false;
    }
    return isAiEmptyValue(insert.values[c.name]);
  });
};

const computeAiFillLabel = (busy: boolean, noEmpties: boolean): string => {
  if (busy) {
    return "Filling with AI…";
  }
  if (noEmpties) {
    return "No empty cells to fill";
  }
  return "Fill empty cells with AI";
};

// KOA-5857 — Table editor parent / composition root.
//
// State and behavior are split across four hooks:
//   * useTableRowsQuery       — fetch + paging + AbortController + refresh
//   * useTableEditorMutations — pending reducer + save lifecycle + atomic toggle
//   * useTableEditorAi        — fill/generate + highlight pulse + shared abort
//   * useTableEditorExport    — Export ▾ menu + per-page/selection export
//
// What stays here: page/size/sort/filter state (toolbar controls live in
// the JSX), selection (it crosses query and context menu), the right-click
// menu state + builders (they read from every hook), and the JSX layout.
//
// Before this split the component was 1,336 lines and any change to one
// concern risked re-renders in unrelated areas. Hooks own their state +
// cleanup; the parent composes.
const TableRowsEditorComponent = forwardRef<ITableRowsEditorHandle, ITableRowsEditorProps>(function TableRowsEditor(
  props: ITableRowsEditorProps,
  ref: React.ForwardedRef<ITableRowsEditorHandle>
): JSX.Element {
  const { tableName, tableSchema, sqlDialect, datasourceId, onClose, onNavigateToTable, initialFilter } = props;
  const { switcherItems, onSwitchTable, embedded, datasourceName } = props;

  // ── Page / sort / filter state ──────────────────────────────────
  // Lives in the parent because the toolbar pagination + filter UI is
  // here and useTableRowsQuery reads them as inputs.
  //
  // On mount we read any persisted view (filters + sort + page size)
  // for this (schema, table). FK-jump initialFilter wins over the
  // persisted filters — the explicit-navigation signal should land on
  // the referenced row, not on whatever filter was set last time the
  // user looked at that table. Page number is NEVER persisted (jumping
  // to page 7 of a different result set feels broken).
  // Scope persisted view state across (datasource, dialect, schema,
  // table) so two projects browsing the same table name don't share a
  // view, "no schema" stays distinct from "dbo", and a SQL Server vs.
  // Postgres backend with overlapping names doesn't pull the wrong
  // filter set.
  const viewScope: IViewScope = useMemo(
    (): IViewScope => ({
      datasourceId,
      dialect: sqlDialect,
      tableSchema,
      tableName
    }),
    [datasourceId, sqlDialect, tableSchema, tableName]
  );
  const persistedView: IPersistedView | null = useMemo(
    (): IPersistedView | null => readPersistedView(viewScope),
    [viewScope]
  );
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(persistedView?.pageSize ?? DEFAULT_PAGE_SIZE);
  const [sort, setSort] = useState<IGridSortState | null>(initialFilter ? null : (persistedView?.sort ?? null));
  // Layout state — column widths the user has manually resized to, and
  // the set of column names the user has hidden. Both seed from the
  // persisted view so a roundtrip restores the user's layout choices
  // alongside their filters / sort / page size.
  const [columnWidths, setColumnWidths] = useState<IColumnWidthMap>(persistedView?.columnWidths ?? {});
  const [hiddenColumns, setHiddenColumns] = useState<string[]>(persistedView?.hiddenColumns ?? []);
  // Anchor + open state for the "Columns" picker dropdown. RowContextMenu
  // is reused (same anchored popup component the toolbar export dropdown
  // uses) so the visual treatment matches the rest of the toolbar.
  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState<IMenuAnchor | null>(null);
  // Anchor for the Save split-button's overflow menu. Holds secondary
  // save-flow actions (Preview SQL today, room for "Save as draft" /
  // "Save and refresh" later) without putting each as a peer button
  // that'd compete with the primary Save for toolbar real estate.
  // Anchor for the discard-confirm popover. Set when the user clicks
  // Discard with enough pending state to warrant a confirmation; cleared
  // when they pick a branch or click outside. See `DISCARD_CONFIRM_THRESHOLD`
  // for the heuristic — below it we discard without asking (undo can
  // rescue a one- or two-edit slip).
  // Pending destructive action: toolbar Discard, editor Close while
  // dirty, table-navigation while dirty — anything that would
  // silently throw away the user's pending edits. `anchor` positions
  // the confirm popover; `proceed` is what to run AFTER discardAll
  // (Discard is just `discardAll`; Close runs `onClose`; navigate
  // runs the swap). Undefined `proceed` means "discard only" (the
  // toolbar Discard's behavior).
  interface IPendingDiscard {
    anchor: IMenuAnchor;
    proceed?: () => void;
  }
  const [pendingDiscard, setPendingDiscard] = useState<IPendingDiscard | null>(null);
  // How the last save ended, held on the Save button long enough to be read (✓ "Saved", or
  // "Try again"), then back to idle. The button is the confirmation now — the editor stays open,
  // so there is nothing for a toast or a separate "Saved" chip to add. `isSaving` wins over it
  // while a save runs; see `saveStatus` below.
  const [settledSaveStatus, setSettledSaveStatus] = useState<KuikaButtonStatus>("idle");

  // Per-column filters — { columnName → contains-query }. Plain object so
  // React shallow-equality re-fires the row fetch only when entries
  // actually change. Debounced before being sent.
  //
  // Seed order: FK-nav `initialFilter` (explicit) wins; otherwise the
  // last persisted view for this table; otherwise empty.
  const seedFilters: IColumnFilterMap = initialFilter
    ? { [initialFilter.column]: { operator: initialFilter.operator ?? "eq", value: initialFilter.value } }
    : (persistedView?.filters ?? {});
  const [columnFilters, setColumnFilters] = useState<IColumnFilterMap>(seedFilters);
  const [debouncedFilters, setDebouncedFilters] = useState<IColumnFilterMap>(seedFilters);
  useEffect((): (() => void) => {
    const t = window.setTimeout((): void => setDebouncedFilters(columnFilters), 220);
    return (): void => window.clearTimeout(t);
  }, [columnFilters]);

  // Persist the view as soon as it stabilises (debounced filters change,
  // sort changes, pageSize changes). Skip the very first render so we
  // don't overwrite a freshly-read view with itself; only write once a
  // real user action has shifted the state. We also skip persistence
  // when an `initialFilter` prop is in play — that's an FK-jump session
  // and shouldn't bleed into the "what was I last looking at" memory
  // for the referenced table.
  const hasPersistedOnceRef = useRef<boolean>(false);
  useEffect((): void => {
    if (!hasPersistedOnceRef.current) {
      hasPersistedOnceRef.current = true;
      return;
    }
    if (initialFilter) {
      return;
    }
    writePersistedView(viewScope, {
      filters: debouncedFilters,
      sort,
      pageSize,
      columnWidths,
      hiddenColumns
    });
  }, [viewScope, debouncedFilters, sort, pageSize, columnWidths, hiddenColumns, initialFilter]);

  // ── Multi-row selection ─────────────────────────────────────────
  // Lives in the parent because it crosses concerns — the query hook
  // prunes stale PKs on refresh, the context menu reads it for export
  // scope, the grid renders it as row highlight.
  const [selectedPks, setSelectedPks] = useState<Set<RowPkString>>(new Set());
  // Anchor for shift-click range selection — the index (on the current
  // page) of the last row the user clicked. Cleared on reload.
  const lastSelectedIndexRef = useRef<number | null>(null);

  // ── Right-click context menu ────────────────────────────────────
  const [contextMenu, setContextMenu] = useState<ContextMenuTarget | null>(null);
  // ── Import modal ────────────────────────────────────────────────
  const [isImportModalOpen, setIsImportModalOpen] = useState<boolean>(false);
  // ── SQL preview modal ──────────────────────────────────────────
  // `null` when closed; non-null hands the same wire-format batch request
  // the actual save would send to the preview endpoint, so the displayed
  // SQL can't drift from the executed SQL.
  const [previewRequest, setPreviewRequest] = useState<IManagedDbBatchRequest | null>(null);

  // ── Hook composition ────────────────────────────────────────────
  const query = useTableRowsQuery({
    tableName,
    tableSchema,
    pageNumber,
    pageSize,
    sort,
    debouncedFilters
  });
  const {
    columns,
    primaryKeyColumns,
    isSystemTable,
    foreignKeys,
    inboundForeignKeys,
    sensitiveHiddenColumns,
    rows,
    totalCount,
    loadedPageNumber,
    loadedPageSize,
    isLoading,
    loadError,
    refreshRows,
    setLoadError
  } = query;

  // System tables are server-marked via the rows-response payload —
  // the editor uses the authoritative flag rather than a K-prefix
  // heuristic that would wrongly lock user tables like KitchenOrders
  // or KPIData. The flag defaults to false until the first response
  // lands; the server still rejects writes to system tables, so any
  // brief pre-response window can't actually corrupt anything.
  const isReadOnly: boolean = isSystemTable;

  // What the grid region draws while rows load. The table's shape is its columns, so "has data"
  // means the columns are known: until then the first load is previewed; after, a reload (page,
  // sort, filter) keeps the rows on screen and only the small ring beside the count says so.
  const rowsPresentation: LoadingPresentation = useLoadingPresentation({ isLoading, hasData: columns.length > 0 });
  const isGridWaiting: boolean = rowsPresentation === "skeleton" || rowsPresentation === "pending";

  // Prune selection on every row refresh — keep only PKs that still
  // exist on the new page so old selections don't pollute a fresh export.
  // Was inline in refreshRows before the hook split; observing rows
  // changes here is cleaner because the hook stays free of cross-concern
  // setState calls.
  useEffect((): void => {
    setSelectedPks((prev: Set<RowPkString>): Set<RowPkString> => {
      if (prev.size === 0) {
        return prev;
      }
      const pks: Set<string> = new Set();
      rows.forEach((r: IManagedDbRow): void => {
        pks.add(rowPkStringFromRow(r, primaryKeyColumns));
      });
      const fresh: Set<RowPkString> = new Set();
      prev.forEach((pk: RowPkString): void => {
        if (pks.has(pk)) {
          fresh.add(pk);
        }
      });
      return fresh.size === prev.size ? prev : fresh;
    });
    lastSelectedIndexRef.current = null;
  }, [rows, primaryKeyColumns]);

  const mutations = useTableEditorMutations({
    tableName,
    tableSchema,
    columns,
    primaryKeyColumns,
    rows,
    refreshRows,
    shortcutsEnabled: embedded ? embedded.isActive : true
  });
  const {
    pending,
    isDirty,
    dirtyCount,
    isSaving,
    saveError,
    saveIssuesByClientId,
    savedAt,
    atomicSave,
    setAtomicSave,
    setSaveError,
    editCell,
    editInsertCell,
    addInsertRow,
    cloneRow,
    addRowFromValues,
    removeInsertRow,
    markRowDeleted,
    unmarkRowDeleted,
    discardAll,
    saveAll,
    validateLocalEdits,
    localValidationIssueCount,
    refreshConflictsByPk,
    undo,
    redo,
    canUndo,
    canRedo,
    endEditSession
  } = mutations;

  const ai = useTableEditorAi({
    tableName,
    tableSchema,
    isReadOnly,
    pending,
    rows,
    columns,
    primaryKeyColumns,
    sensitiveHiddenColumns,
    editCell,
    editInsertCell,
    addRowFromValues
  });
  const {
    aiBusy,
    aiNotice,
    setAiNotice,
    isAiModalOpen,
    setIsAiModalOpen,
    highlightedInsertIds,
    aiFillRow,
    aiFillInsertRow,
    aiGenerateRows
  } = ai;

  const exportHook = useTableEditorExport({
    tableName,
    tableSchema,
    sqlDialect,
    primaryKeyColumns,
    hasNonPkColumns: columns.length > primaryKeyColumns.length,
    sort,
    debouncedFilters,
    totalRowCount: totalCount,
    columns,
    onExportError: setLoadError
  });
  const {
    exportAnchor,
    openExportMenu,
    closeExportMenu,
    exportAllMenuOptions,
    bufferedExportConfirm,
    isExporting,
    runExport
  } = exportHook;

  // ── Toolbar callbacks ───────────────────────────────────────────
  const onSortChange = useCallback((next: IGridSortState | null): void => {
    setSort(next);
    setPageNumber(1);
  }, []);

  // Per-column filter callbacks. Jumping the page back to 1 whenever a
  // filter changes prevents the "I filtered but the new result-set is
  // smaller than my current page index so I see nothing" trap.
  // `null` clears the column entirely (used when the user empties the
  // value input or picks a non-valueless operator with no value yet).
  const setColumnFilter = useCallback((column: string, next: IColumnFilterEntry | null): void => {
    setColumnFilters((prev: IColumnFilterMap): IColumnFilterMap => {
      const current: IColumnFilterEntry | undefined = prev[column];
      if (next === null) {
        if (current === undefined) {
          return prev;
        }
        const cleared: IColumnFilterMap = { ...prev };
        delete cleared[column];
        return cleared;
      }
      if (current && current.operator === next.operator && current.value === next.value) {
        return prev;
      }
      return { ...prev, [column]: next };
    });
    setPageNumber(1);
  }, []);

  const clearAllFilters = useCallback((): void => {
    setColumnFilters({});
    setPageNumber(1);
  }, []);

  // Width-change handler — round to the nearest integer before persisting
  // (mousemove deltas are sub-pixel and a fractional px in localStorage
  // serializes longer than the value is worth).
  const setColumnWidth = useCallback((column: string, width: number): void => {
    setColumnWidths((prev: IColumnWidthMap): IColumnWidthMap => ({
      ...prev,
      [column]: Math.round(width)
    }));
  }, []);

  // Hidden-column set reconciled against the currently-loaded schema —
  // a column the user hid yesterday that's since been dropped (or
  // renamed) is silently filtered out here so the grid doesn't carry
  // ghosts in its visible/hidden math.
  const effectiveHiddenColumns: string[] = useMemo((): string[] => {
    if (hiddenColumns.length === 0 || columns.length === 0) {
      return [];
    }
    const known: Set<string> = new Set(columns.map((c: IDatabaseColumnModel): string => c.name));
    return hiddenColumns.filter((name: string): boolean => known.has(name));
  }, [hiddenColumns, columns]);

  // Toggle a column in / out of the hidden set. Used by the "Columns"
  // dropdown checkbox; never auto-hides via any other path. Refuses to
  // hide the last visible column — leaving the grid with zero visible
  // columns would render an empty body with no obvious way back.
  const toggleColumnHidden = useCallback(
    (column: string): void => {
      setHiddenColumns((prev: string[]): string[] => {
        if (prev.includes(column)) {
          return prev.filter((c: string): boolean => c !== column);
        }
        // Compute visible-after-hide from the CURRENT schema, not the
        // persisted set — same reconciliation as effectiveHiddenColumns.
        const known: Set<string> = new Set(columns.map((c: IDatabaseColumnModel): string => c.name));
        const effectiveHiddenAfter: Set<string> = new Set(prev.filter((c: string): boolean => known.has(c)));
        effectiveHiddenAfter.add(column);
        const visibleAfter: number = columns.filter(
          (c: IDatabaseColumnModel): boolean => !effectiveHiddenAfter.has(c.name)
        ).length;
        if (visibleAfter === 0) {
          return prev;
        }
        return [...prev, column];
      });
    },
    [columns]
  );

  // A filter "counts" as active when it would actually constrain the
  // result set: valueless operators (IS NULL / IS NOT NULL) always do;
  // value-bound operators only count once the user typed something.
  const activeFilterCount: number = Object.values(columnFilters).filter(
    (entry: IColumnFilterEntry | undefined): boolean => {
      if (!entry) {
        return false;
      }
      if (isValuelessOperator(entry.operator)) {
        return true;
      }
      return entry.value.trim().length > 0;
    }
  ).length;

  // ── Selection (DataGrip-style row-gutter click) ─────────────────
  // Plain click → replace selection with this row.
  // Cmd / Ctrl click → toggle this row in/out of the selection.
  // Shift click → range-select from the last anchor to the clicked row.
  const handleRowSelectionClick = useCallback(
    (pksOnPage: RowPkString[], clickedIndex: number, modifiers: ISelectionModifiers): void => {
      const clickedPk: RowPkString | undefined = pksOnPage[clickedIndex];
      if (clickedPk === undefined) {
        return;
      }
      const anchor: number | null = lastSelectedIndexRef.current;
      setSelectedPks((prev: Set<RowPkString>): Set<RowPkString> => {
        if (modifiers.shift && anchor !== null) {
          const lo: number = Math.min(anchor, clickedIndex);
          const hi: number = Math.max(anchor, clickedIndex);
          const next: Set<RowPkString> = new Set(prev);
          for (let i = lo; i <= hi; i++) {
            const pk: RowPkString | undefined = pksOnPage[i];
            if (pk !== undefined) {
              next.add(pk);
            }
          }
          return next;
        }
        if (modifiers.meta) {
          const next: Set<RowPkString> = new Set(prev);
          if (next.has(clickedPk)) {
            next.delete(clickedPk);
          } else {
            next.add(clickedPk);
          }
          return next;
        }
        // Plain click — replace selection with this one row, or clear if
        // it's the only row currently selected (toggle-off semantics).
        if (prev.size === 1 && prev.has(clickedPk)) {
          return new Set();
        }
        return new Set([clickedPk]);
      });
      // Shift extends from the existing anchor; anything else resets it.
      if (!modifiers.shift) {
        lastSelectedIndexRef.current = clickedIndex;
      }
    },
    []
  );

  const handleSelectAllOnPage = useCallback((pksOnPage: RowPkString[]): void => {
    setSelectedPks((prev: Set<RowPkString>): Set<RowPkString> => {
      const allSelected: boolean = pksOnPage.length > 0 && pksOnPage.every((pk: RowPkString): boolean => prev.has(pk));
      if (allSelected) {
        const next: Set<RowPkString> = new Set(prev);
        pksOnPage.forEach((pk: RowPkString): void => {
          next.delete(pk);
        });
        return next;
      }
      const next: Set<RowPkString> = new Set(prev);
      pksOnPage.forEach((pk: RowPkString): void => {
        next.add(pk);
      });
      return next;
    });
    lastSelectedIndexRef.current = null;
  }, []);

  // ── Context menu callbacks ──────────────────────────────────────
  const openContextMenu = useCallback(
    (clientX: number, clientY: number, pk: RowPkString, columnName?: string): void => {
      // Right-clicking an unselected row promotes it to a single-row
      // selection (matches DataGrip + Excel behavior).
      setSelectedPks((prev: Set<RowPkString>): Set<RowPkString> => {
        if (prev.has(pk)) {
          return prev;
        }
        return new Set([pk]);
      });
      setContextMenu({ kind: "row", x: clientX, y: clientY, pk, columnName });
    },
    []
  );

  const openInsertContextMenu = useCallback(
    (clientX: number, clientY: number, clientId: string, columnName?: string): void => {
      // Insert rows aren't part of selectedPks (selection is PK-keyed and
      // these have no PK yet), so there's nothing to promote — just open.
      setContextMenu({ kind: "insert", x: clientX, y: clientY, clientId, columnName });
    },
    []
  );

  const closeContextMenu = useCallback((): void => {
    setContextMenu(null);
  }, []);

  // ── Right-click "Export …" handler ──────────────────────────────
  // Wraps the export hook's runExport with the parent's per-context
  // concerns: derive rowsToExport from selection vs page, route errors
  // to the load-error banner, close the context menu when done.
  const runExportForContextMenu = useCallback(
    async (format: "csv" | "xlsx" | "insert" | "update" | "upsert"): Promise<void> => {
      // No selection → operate over all rows currently visible on this
      // page. Same convention as DataGrip's "no selection means everything".
      const rowsToExport: IManagedDbRow[] =
        selectedPks.size === 0
          ? rows
          : rows.filter((r: IManagedDbRow): boolean => selectedPks.has(rowPkStringFromRow(r, primaryKeyColumns)));
      try {
        await runExport(format, rowsToExport, columns);
      } catch (e: unknown) {
        const alert: ITableEditorAlert = await buildTableEditorErrorAlert(e, {
          operation: "export",
          title: "Export failed",
          fallbackMessage: "The current page or selection could not be exported."
        });
        setLoadError(alert);
      } finally {
        closeContextMenu();
      }
    },
    [selectedPks, rows, primaryKeyColumns, columns, runExport, setLoadError, closeContextMenu]
  );

  // ── Import landing ──────────────────────────────────────────────
  // After a successful backend import the row reducer is the wrong place
  // to land — the rows are already persisted, not pending. Reload the
  // grid and surface the summary in the same notice banner the AI
  // helpers use.
  const handleImportComplete = useCallback(
    (result: IImportSummary): void => {
      const parts: string[] = [];
      parts.push(
        `Imported ${result.inserted} row${result.inserted === 1 ? "" : "s"}.${result.failed > 0 ? ` ${result.failed} failed.` : ""}`
      );
      if (result.warnings.length > 0) {
        parts.push(result.warnings[0]!);
      }
      setAiNotice(
        createTableEditorAlert({
          tone: result.failed > 0 ? "warn" : "success",
          operation: "import",
          title: result.failed > 0 ? "Import completed with failures" : "Import complete",
          message: parts.join(" "),
          details: result.warnings.slice(1)
        })
      );
      void refreshRows();
    },
    [refreshRows, setAiNotice]
  );

  // ── Context-menu builders ───────────────────────────────────────
  // Lazy because the right-click target row matters and the menu options
  // are recomputed per open. Persisted rows + pending inserts get
  // different verb sets.
  const buildRowContextMenu = (targetPk: RowPkString, clickedColumn?: string): IRowContextMenuOption[] => {
    // Right-click export = current page or current selection. Toolbar
    // export is the "all filtered rows" path; labels make the contrast
    // obvious.
    const exportCount: number = selectedPks.size === 0 ? rows.length : selectedPks.size;
    const exportLabel: string = selectedPks.size === 0 ? "current page" : "selection";
    const hasPk: boolean = primaryKeyColumns.length > 0;
    // UPDATE export emits SET <col> = <val> for every non-PK column;
    // a PK-only table would produce empty SET → invalid SQL. Gate the
    // menu entry on having at least one mutable column. UPSERT has the
    // same vacuous case (DO NOTHING on every conflict), so it's
    // similarly gated.
    const hasNonPkColumns: boolean = columns.length > primaryKeyColumns.length;

    const rightClickedRow: IManagedDbRow | undefined = rows.find(
      (r: IManagedDbRow): boolean => rowPkStringFromRow(r, primaryKeyColumns) === targetPk
    );
    const canClone: boolean = !isReadOnly && hasPk && rightClickedRow !== undefined;

    // Bulk-row affordances over the current selection. Show only when
    // the selection isn't empty so the menu stays compact on the common
    // "right-click a single row" path. PK + writable-table gates match
    // the per-row counterparts (Clone / Delete row), and we capture the
    // selection size into the label so users see the scope before
    // committing to a destructive action.
    const selectionSize: number = selectedPks.size;
    const canBulkAct: boolean = !isReadOnly && hasPk && selectionSize > 0;
    // Selected rows mapped to their IManagedDbRow — the selection-prune
    // effect (rows useEffect) keeps selectedPks aligned to what's
    // currently rendered, so every PK in the set should resolve. Defensive
    // filter against any stale entry between refresh + prune.
    const selectedRows: IManagedDbRow[] = canBulkAct
      ? rows.filter((r: IManagedDbRow): boolean => selectedPks.has(rowPkStringFromRow(r, primaryKeyColumns)))
      : [];

    // FK navigation — one menu item per FK column on the clicked row
    // that holds a non-null value. If onNavigateToTable isn't wired,
    // hide the lot.
    const fkNavOptions: IRowContextMenuOption[] = [];
    if (onNavigateToTable && rightClickedRow) {
      foreignKeys.forEach((fk: IManagedDbForeignKey): void => {
        const val: unknown = rightClickedRow[fk.columnName];
        if (val === null || val === undefined || val === "") {
          return;
        }
        fkNavOptions.push({
          type: "item",
          label: `${fk.columnName} → ${fk.refTableName}`,
          onClick: (): void => {
            // Navigation swaps the editor's table identity, which the
            // mutations hook treats as a discardAll trigger. Without
            // the guard the user's pending edits would silently
            // disappear; guardDestructive shows the same Discard
            // confirm popover the toolbar uses.
            const proceed = (): void => {
              // refColumnName is populated server-side by looking up
              // the referenced table's PK; "Id" remains as a fallback
              // for older payloads.
              onNavigateToTable(
                { name: fk.refTableName, schema: fk.refTableSchema },
                // FK lookups want an exact match on the referenced PK.
                // Earlier versions of the editor hard-coded the
                // operator to "contains" server-side, which silently
                // widened these jumps to substring matches.
                { column: fk.refColumnName ?? "Id", value: String(val), operator: "eq" }
              );
            };
            closeContextMenu();
            guardDestructive(contextMenu?.x ?? 0, contextMenu?.y ?? 0, proceed);
          }
        });
      });
    }

    // Reverse-FK navigation — for each other table that points at
    // the current row's referenced column (almost always the PK),
    // open the referencing table pre-filtered by FromColumn =
    // currentRow[RefColumn]. Skipped when the referenced value is
    // missing — without a value to bind, the filter would degrade to
    // "all rows" and the affordance becomes a footgun. Multiple FKs
    // from the same source table to the same target collapse to
    // distinct entries by their source column name.
    const inboundNavOptions: IRowContextMenuOption[] = [];
    if (onNavigateToTable && rightClickedRow) {
      inboundForeignKeys.forEach((fk: IManagedDbInboundForeignKey): void => {
        const val: unknown = rightClickedRow[fk.refColumnName];
        if (val === null || val === undefined || val === "") {
          return;
        }
        inboundNavOptions.push({
          type: "item",
          label: `${fk.fromTableName} (via ${fk.fromColumnName})`,
          onClick: (): void => {
            const proceed = (): void => {
              onNavigateToTable(
                { name: fk.fromTableName, schema: fk.fromTableSchema },
                { column: fk.fromColumnName, value: String(val), operator: "eq" }
              );
            };
            closeContextMenu();
            guardDestructive(contextMenu?.x ?? 0, contextMenu?.y ?? 0, proceed);
          }
        });
      });
    }

    const busyForThisRow: boolean = aiBusy?.kind === "row" && aiBusy.pk === targetPk;
    // Mirror the server-side SelectFillableColumns gate: identity /
    // auto-increment are DB-assigned; Guid PKs are server-stamped. Empties
    // on those columns don't count toward "has something to fill".
    const noEmptyCells: boolean =
      rightClickedRow !== undefined && !rowHasFillableEmpties(rightClickedRow, targetPk, columns, pending);
    const aiDisabled: boolean = isReadOnly || aiBusy !== null || rightClickedRow === undefined || noEmptyCells;
    const aiFillLabel: string = computeAiFillLabel(busyForThisRow, noEmptyCells);

    // Export submenu is suppressed on system tables to match the
    // "cannot be... exported" wording in the read-only banner. The
    // toolbar Export ▾ is gated the same way; FK navigation and
    // browse-only operations stay available.
    const exportSubmenu: IRowContextMenuOption[] = isReadOnly
      ? []
      : [
          {
            type: "submenu",
            label: `Export ${exportLabel} (${exportCount})`,
            options: [
              // Mirror the toolbar Export ▾ button's `disabled={isExporting}`
              // gate. The right-click path runs a client-side serialisation
              // (downloadXlsx dynamic-imports exceljs, csv builds in main-
              // thread string concatenation) that can compete with an
              // in-flight buffered backend export for memory and main-
              // thread time — exactly the OOM the gate exists to prevent.
              {
                type: "item",
                label: "CSV",
                onClick: (): void => void runExportForContextMenu("csv"),
                disabled: isExporting
              },
              {
                type: "item",
                label: "Excel (XLSX)",
                onClick: (): void => void runExportForContextMenu("xlsx"),
                disabled: isExporting
              },
              { type: "separator" },
              {
                type: "item",
                label: "SQL · INSERT",
                onClick: (): void => void runExportForContextMenu("insert"),
                disabled: isExporting
              },
              {
                type: "item",
                label: "SQL · UPDATE",
                onClick: (): void => void runExportForContextMenu("update"),
                disabled: isExporting || !hasPk || !hasNonPkColumns
              },
              {
                // UPSERT only needs a PK — on a PK-only table the
                // emitted SQL degrades cleanly to insert-only
                // (Postgres DO NOTHING, SQL Server / Oracle MERGE
                // without WHEN MATCHED). Don't gate on
                // hasNonPkColumns; UPDATE is the one that breaks
                // without a SET target.
                type: "item",
                label: "SQL · UPSERT (MERGE)",
                onClick: (): void => void runExportForContextMenu("upsert"),
                disabled: isExporting || !hasPk
              }
            ]
          },
          { type: "separator" }
        ];

    // Cell-scoped "Set null" entry — appears at the top so the
    // user's eye lands on it for the most common case (right-clicked
    // a specific cell wanting to clear it).
    //
    // Always visible when the click resolves to a column, even when
    // the action isn't possible (non-nullable column, PK / identity,
    // already null). The disabled state + tooltip explain the
    // reason: "Column is not nullable", "Already null", etc. The
    // earlier conditional render was hiding the entry entirely when
    // it wasn't applicable, which let users conclude the feature
    // didn't exist; surfacing it with a reason is more discoverable
    // and self-documenting. Without this entry, non-string nullable
    // cells had no obvious path to null — numeric / date / Guid /
    // FK / Location inputs all need keyboard-clearing dance the
    // user has to guess at.
    const setNullEntries: IRowContextMenuOption[] = [];
    if (!isReadOnly && typeof clickedColumn === "string" && rightClickedRow) {
      const col: IDatabaseColumnModel | undefined = columns.find(
        (c: IDatabaseColumnModel): boolean => c.name === clickedColumn
      );
      if (col !== undefined) {
        const current: unknown = rightClickedRow[clickedColumn];
        const alreadyNull: boolean = current === null || current === undefined;
        let disabledReason: string | undefined;
        if (col.isPk === true) {
          disabledReason = "Primary key columns can't be cleared";
        } else if (col.isIdentity === true || col.isAutoIncrement === true) {
          disabledReason = "Auto-generated columns can't be cleared";
        } else if (col.nullable === false) {
          disabledReason = "Column is not nullable";
        } else if (alreadyNull) {
          disabledReason = "Already null";
        }
        setNullEntries.push({
          type: "item",
          label: `Set ${clickedColumn} to null`,
          disabled: disabledReason !== undefined,
          title: disabledReason,
          onClick: (): void => {
            editCell(targetPk, clickedColumn, null, current);
            closeContextMenu();
          }
        });
        setNullEntries.push({ type: "separator" });
      }
    }

    return [
      ...setNullEntries,
      ...exportSubmenu,
      ...(fkNavOptions.length > 0
        ? ([{ type: "submenu", label: "Go to referenced row", options: fkNavOptions }] as IRowContextMenuOption[])
        : []),
      ...(inboundNavOptions.length > 0
        ? ([{ type: "submenu", label: "Referenced by", options: inboundNavOptions }] as IRowContextMenuOption[])
        : []),
      {
        type: "item",
        label: aiFillLabel,
        disabled: aiDisabled,
        onClick: (): void => {
          void aiFillRow(targetPk);
          closeContextMenu();
        }
      },
      {
        type: "item",
        label: "Clone row",
        disabled: !canClone,
        onClick: (): void => {
          if (rightClickedRow) {
            cloneRow(rightClickedRow);
          }
          closeContextMenu();
        }
      },
      ...(canBulkAct
        ? ([
            { type: "separator" },
            {
              type: "submenu",
              label: `Fill column from this row → ${selectionSize} selected`,
              // One submenu item per editable column on the right-clicked
              // row. Picking a column dispatches editCell for every
              // selected pk with the right-clicked row's value for that
              // column. The reducer captures each row's original value
              // on first touch so optimistic-concurrency snapshots stay
              // correct. PK / identity / auto-increment columns are
              // excluded — the SQL builder rejects updates to those
              // anyway and the cell renders read-only.
              options: rightClickedRow
                ? primaryKeyColumns.length > 0
                  ? buildFillColumnOptions(rightClickedRow, columns, selectedRows, primaryKeyColumns, editCell)
                  : [{ type: "item", label: "Needs primary key", disabled: true, onClick: (): void => undefined }]
                : [{ type: "item", label: "No source row", disabled: true, onClick: (): void => undefined }]
            },
            {
              type: "item",
              label: `Duplicate ${selectionSize} selected`,
              onClick: (): void => {
                // Each cloneRow dispatch produces a fresh insert with a
                // new tmp clientId, so duplicating N selected rows lands
                // N new pending inserts without colliding. Identity /
                // auto-increment columns are stripped inside cloneRow so
                // the DB still assigns fresh values on Save.
                selectedRows.forEach((r: IManagedDbRow): void => {
                  cloneRow(r);
                });
                closeContextMenu();
              }
            },
            {
              type: "item",
              label: `Delete ${selectionSize} selected`,
              onClick: (): void => {
                // markRowDeleted is a soft-delete in the pending reducer
                // — rows render struck-through until Save commits them.
                // Selection stays as-is so the user can review the strike
                // before committing; clear it explicitly via "Clear
                // selection" below or by paging away.
                selectedPks.forEach((pk: RowPkString): void => {
                  markRowDeleted(pk);
                });
                closeContextMenu();
              }
            }
          ] as IRowContextMenuOption[])
        : []),
      { type: "separator" },
      {
        type: "item",
        label: "Clear selection",
        disabled: selectedPks.size === 0,
        onClick: (): void => {
          setSelectedPks(new Set());
          closeContextMenu();
        }
      }
    ];
  };

  // Menu for a pending insert — narrower scope. No PK to export by, no
  // FK navigation (source row isn't saved yet so "go to" is meaningless),
  // no multi-row selection to clear. Useful verbs: AI fill, Clone, Discard.
  const buildInsertContextMenu = (targetClientId: string, clickedColumn?: string): IRowContextMenuOption[] => {
    const insert: IPendingRowInsert | undefined = pending.inserts.find(
      (i: IPendingRowInsert): boolean => i.clientId === targetClientId
    );
    const busyForThisInsert: boolean = aiBusy?.kind === "insert" && aiBusy.clientId === targetClientId;
    const noEmptyCells: boolean = insert !== undefined && !insertHasFillableEmpties(insert, columns);
    const aiDisabled: boolean = isReadOnly || aiBusy !== null || insert === undefined || noEmptyCells;
    const aiFillLabel: string = computeAiFillLabel(busyForThisInsert, noEmptyCells);

    // Cell-scoped "Set null" entry for the insert path — same
    // semantics as the persisted-row version. Always visible when a
    // column resolves, disabled with an explanatory tooltip when
    // ineligible so the affordance is discoverable.
    const setNullEntries: IRowContextMenuOption[] = [];
    if (!isReadOnly && typeof clickedColumn === "string" && insert !== undefined) {
      const col: IDatabaseColumnModel | undefined = columns.find(
        (c: IDatabaseColumnModel): boolean => c.name === clickedColumn
      );
      if (col !== undefined) {
        const current: unknown = insert.values[clickedColumn];
        const alreadyNull: boolean = current === null || current === undefined;
        let disabledReason: string | undefined;
        if (col.isPk === true) {
          disabledReason = "Primary key columns can't be cleared";
        } else if (col.isIdentity === true || col.isAutoIncrement === true) {
          disabledReason = "Auto-generated columns can't be cleared";
        } else if (col.nullable === false) {
          disabledReason = "Column is not nullable";
        } else if (alreadyNull) {
          disabledReason = "Already null";
        }
        setNullEntries.push({
          type: "item",
          label: `Set ${clickedColumn} to null`,
          disabled: disabledReason !== undefined,
          title: disabledReason,
          onClick: (): void => {
            editInsertCell(targetClientId, clickedColumn, null);
            closeContextMenu();
          }
        });
        setNullEntries.push({ type: "separator" });
      }
    }

    return [
      ...setNullEntries,
      {
        type: "item",
        label: aiFillLabel,
        disabled: aiDisabled,
        onClick: (): void => {
          void aiFillInsertRow(targetClientId);
          closeContextMenu();
        }
      },
      {
        type: "item",
        label: "Clone row",
        disabled: isReadOnly || insert === undefined,
        onClick: (): void => {
          if (insert) {
            // Clone takes IManagedDbRow; insert.values is exactly that shape.
            cloneRow(insert.values);
          }
          closeContextMenu();
        }
      },
      { type: "separator" },
      {
        type: "item",
        label: "Discard this row",
        disabled: insert === undefined,
        onClick: (): void => {
          removeInsertRow(targetClientId);
          closeContextMenu();
        }
      }
    ];
  };

  const contextMenuOptions: IRowContextMenuOption[] = (() => {
    if (!contextMenu) {
      return [];
    }
    if (contextMenu.kind === "row") {
      return buildRowContextMenu(contextMenu.pk, contextMenu.columnName);
    }
    return buildInsertContextMenu(contextMenu.clientId, contextMenu.columnName);
  })();

  // ── Derived ─────────────────────────────────────────────────────
  const noPrimaryKey: boolean = primaryKeyColumns.length === 0 && columns.length > 0;

  // Single per-row issue channel for the grid. Three sources feed it:
  //   * server-returned per-row failures from the last save (highest
  //     priority — concrete reasons from the database)
  //   * local-validation issues (also in saveIssuesByClientId today)
  //   * refresh-time conflict warnings, derived from current rows vs.
  //     pending originals
  // Server / local wins on key collision; refresh-only conflicts fill
  // the gaps for rows that haven't yet been touched by a save attempt.
  const gridIssuesByClientId: Map<string, string> = useMemo((): Map<string, string> => {
    if (refreshConflictsByPk.size === 0) {
      return saveIssuesByClientId;
    }
    const merged: Map<string, string> = new Map<string, string>(refreshConflictsByPk);
    saveIssuesByClientId.forEach((message: string, clientId: string): void => {
      merged.set(clientId, message);
    });
    return merged;
  }, [saveIssuesByClientId, refreshConflictsByPk]);

  // Screen-reader announcement for what the Save button cannot say itself. The button's own
  // status is a polite live region ("Saving", "Saved", "Try again"), so this adds only the
  // count while a save runs and the reason a save failed. aria-live="polite" announces the
  // string whenever it changes, so derived purely from the current mutations-hook state. Empty
  // string suppresses the announcement when nothing newsworthy is happening.
  let liveSaveAnnouncement: string;
  if (isSaving) {
    liveSaveAnnouncement = dirtyCount === 1 ? "Saving 1 change." : `Saving ${EN_US_COUNT.format(dirtyCount)} changes.`;
  } else if (saveError !== null) {
    // Title is the user-facing headline already authored for visual
    // display (e.g. "Some changes could not be saved"). Re-using it
    // here keeps the AT message and the visual message in sync.
    liveSaveAnnouncement = `Save failed. ${saveError.title}.`;
  } else {
    liveSaveAnnouncement = "";
  }

  // The mutations hook owns the save and reports how it ended through `savedAt` (a new stamp per
  // success) and `saveError` (a new alert per failure). Each one is held on the button for its own
  // hold, then released. Driven from the hook's outputs rather than from the click, so ⌘S and the
  // alert's "Retry save" confirm on the same button.
  useEffect((): (() => void) | void => {
    if (savedAt === null) {
      return;
    }
    setSettledSaveStatus("success");
    const t: number = window.setTimeout((): void => {
      setSettledSaveStatus("idle");
    }, KUIKA_BUTTON_SUCCESS_HOLD_MS);
    return (): void => {
      window.clearTimeout(t);
    };
  }, [savedAt]);
  useEffect((): (() => void) | void => {
    if (saveError === null) {
      return;
    }
    setSettledSaveStatus("error");
    const t: number = window.setTimeout((): void => {
      setSettledSaveStatus("idle");
    }, KUIKA_BUTTON_ERROR_HOLD_MS);
    return (): void => {
      window.clearTimeout(t);
    };
  }, [saveError]);
  const saveStatus: KuikaButtonStatus = isSaving ? "loading" : settledSaveStatus;
  const toolbarTitle: string = tableSchema && tableSchema.length > 0 ? `${tableSchema}.${tableName}` : tableName;
  const totalPages: number = totalCount > 0 ? Math.max(1, Math.ceil(totalCount / pageSize)) : 1;
  const canDiscard: boolean = isDirty || pending.inserts.length > 0;

  // ── Embedded: what the host sees ────────────────────────────────
  // The host's close guard reads this, and it runs in the host's
  // componentDidUpdate — the same commit as, say, a discard confirmed
  // right before an FK jump. A passive effect would report after that
  // guard had already read the old "dirty" and asked a second time, so
  // this is a layout effect: children's run before the parent's update.
  // Keyed on the primitives and the callback, not on the `embedded`
  // object, which a host may rebuild on every render.
  const onPendingChange: ((summary: ITableRowsPendingSummary) => void) | undefined = embedded?.onPendingChange;
  useLayoutEffect((): void => {
    onPendingChange?.({ isDirty, dirtyCount, isSaving });
  }, [onPendingChange, isDirty, dirtyCount, isSaving]);

  useImperativeHandle(
    ref,
    (): ITableRowsEditorHandle => ({
      saveAll,
      discardAll
    }),
    [saveAll, discardAll]
  );

  // ── Unsaved-edit guards (data-loss prevention) ──────────────────
  // Two paths to silent data loss the editor previously didn't
  // protect against: browser-level navigation (tab close, reload,
  // window close, back button) and in-app close (the toolbar Close
  // button next to the title). Both used to drop pending edits
  // with no prompt. Apple bar = zero silent data loss.
  //
  // Browser-level: `beforeunload` is the only API that can prompt
  // the user mid-navigation. Modern browsers ignore custom strings
  // and show their own "Leave site?" dialog; we just have to set
  // returnValue so the dialog fires. Active only while dirty so
  // clean sessions navigate without friction.
  useEffect((): (() => void) | undefined => {
    if (!canDiscard || isSaving) {
      return undefined;
    }
    const handler = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
      // Chromium needs returnValue set explicitly. The text is
      // ignored by every modern browser but assigning it is what
      // arms the prompt.
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return (): void => window.removeEventListener("beforeunload", handler);
  }, [canDiscard, isSaving]);

  // In-app: wrap any "leave the editor" affordance so it shows the
  // existing DiscardConfirmPopover when there are unsaved edits.
  // Clean sessions skip the confirm. Anchors the popover at the
  // click coordinates so it appears near the affordance the user
  // actually pressed.
  const guardDestructive = useCallback(
    (clientX: number, clientY: number, proceed: () => void): void => {
      if (!canDiscard) {
        proceed();
        return;
      }
      setPendingDiscard({ anchor: { x: clientX, y: clientY }, proceed });
    },
    [canDiscard]
  );
  const loadErrorActions: ITableEditorAlertAction[] = [
    {
      label: "Retry",
      onClick: (): void => {
        void refreshRows();
      }
    }
  ];
  if (activeFilterCount > 0) {
    loadErrorActions.push({
      label: "Clear filters",
      onClick: clearAllFilters
    });
  }
  // Both Save and Preview SQL ride the same local-validation gate so the
  // user never sees a SQL preview built from cells the backend would
  // reject. validateLocalEdits returns false and raises the alert itself
  // when issues exist; we just bail.
  const openSqlPreview = (): void => {
    if (!validateLocalEdits()) {
      return;
    }
    setPreviewRequest(buildBatchRequestFromPending(tableName, tableSchema, pending, atomicSave));
  };
  // When the alert is sitting on local validation issues, neither Retry
  // save nor Preview SQL helps — both routes hit the same gate and would
  // just re-raise the same alert. Hide them so the action bar only offers
  // what the user can actually do (dismiss + fix the highlighted cells).
  // The toolbar already exposes a Refresh button, so a separate "Reload
  // rows" inside the alert was redundant — dropped entirely.
  const saveErrorActions: ITableEditorAlertAction[] =
    localValidationIssueCount > 0
      ? []
      : [
          {
            label: "Retry save",
            disabled: !isDirty || isSaving,
            onClick: (): void => {
              void saveAll();
            }
          },
          {
            label: "Preview SQL",
            disabled: !isDirty,
            onClick: openSqlPreview
          }
        ];

  // The save's alternatives, under the split button's chevron.
  const saveMenuOptions: KuikaDropdownMenuItemType[] = [
    {
      key: "preview-sql",
      label: "Preview SQL",
      disabled: !isDirty,
      onClick: openSqlPreview,
      dataTestId: "tableEditorPreviewSqlMenuItem",
      showDividerAfter: true
    },
    {
      // Atomic-save toggle. Mirrors the Columns picker's checkmark convention (`✓ Label` vs
      // `   Label`) so the current state is visible at a glance, and stays open on a click so the
      // user can toggle without re-opening the menu.
      key: "atomic-save",
      label: `${atomicSave ? "✓  " : "    "}All-or-nothing save`,
      keepOpenOnSelect: true,
      disabled: isSaving,
      onClick: (): void => setAtomicSave(!atomicSave)
    }
  ];

  // ── Bar content ─────────────────────────────────────────────────
  // Drawn in two places: the standalone editor's own bar, and the strip
  // the embedded editor wears under the table editor's bar. Split into
  // the groups the two arrange differently; the standalone bar composes
  // them exactly as it always has.
  const renderMeta = (): React.ReactNode => (
    <span className="kuika-table-editor__toolbar-meta">
      <KuikaNumber value={totalCount} /> {totalCount === 1 ? "row" : "rows"} · {columns.length} cols
      {rowsPresentation === "refreshing" && <KuikaLoader size="sm" label="Loading rows" />}
    </span>
  );

  // What the rows are right now: read-only, columns held back, filters on.
  const renderStatus = (): React.ReactNode => (
    <>
      {isReadOnly && <KuikaBadge content="Read-only" color="grey" />}
      {sensitiveHiddenColumns.length > 0 && (
        <KuikaTooltip content={`Hidden for security: ${sensitiveHiddenColumns.join(", ")}`} position="bottom">
          <span
            className="kuika-table-editor__sensitive-hidden-badge"
            aria-label={`${sensitiveHiddenColumns.length} sensitive ${sensitiveHiddenColumns.length === 1 ? "column" : "columns"} hidden`}
          >
            {sensitiveHiddenColumns.length} sensitive {sensitiveHiddenColumns.length === 1 ? "column" : "columns"}{" "}
            hidden
          </span>
        </KuikaTooltip>
      )}
      {activeFilterCount > 0 && (
        <KuikaButton type="text" size="sm" onClick={clearAllFilters}>
          Clear {activeFilterCount} filter{activeFilterCount === 1 ? "" : "s"}
        </KuikaButton>
      )}
    </>
  );

  const renderTools = (): React.ReactNode => (
    <>
      <KuikaTooltip content="Undo (⌘Z / Ctrl+Z)" position="bottom">
        <KuikaButton
          type="tertiary"
          size="sm"
          isIcon
          className="kuika-table-editor__toolbar-action-button"
          data-testid="tableEditorUndoButton"
          color="neutral"
          disabled={!canUndo || isSaving}
          onClick={undo}
        >
          <UndoIcon className="kuika-table-editor__toolbar-action-icon" />
        </KuikaButton>
      </KuikaTooltip>
      <KuikaTooltip content="Redo (⌘⇧Z / Ctrl+Shift+Z)" position="bottom">
        <KuikaButton
          type="tertiary"
          size="sm"
          isIcon
          className="kuika-table-editor__toolbar-action-button"
          data-testid="tableEditorRedoButton"
          color="neutral"
          disabled={!canRedo || isSaving}
          onClick={redo}
        >
          <RedoIcon className="kuika-table-editor__toolbar-action-icon" />
        </KuikaButton>
      </KuikaTooltip>
      <KuikaTooltip content="Refresh" position="bottom">
        <KuikaButton
          type="tertiary"
          size="sm"
          isIcon
          className="kuika-table-editor__toolbar-action-button"
          data-testid="tableEditorRefreshButton"
          color="neutral"
          disabled={isLoading}
          onClick={(): void => {
            void refreshRows();
          }}
        >
          <ReloadRowsIcon className="kuika-table-editor__toolbar-action-icon" />
        </KuikaButton>
      </KuikaTooltip>
      <KuikaTooltip
        content={
          effectiveHiddenColumns.length > 0
            ? `Show / hide columns · ${columns.length - effectiveHiddenColumns.length}/${columns.length} visible`
            : "Show / hide columns"
        }
        position="bottom"
      >
        <KuikaButton
          type="tertiary"
          size="sm"
          isIcon
          className="kuika-table-editor__toolbar-action-button"
          color="neutral"
          disabled={isLoading || columns.length === 0}
          onClick={(e: React.MouseEvent<HTMLElement>): void => {
            const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
            setColumnsMenuAnchor({ x: rect.left, y: rect.bottom + 4 });
          }}
        >
          <ColumnsIcon className="kuika-table-editor__toolbar-action-icon" />
        </KuikaButton>
      </KuikaTooltip>
      {!isReadOnly && (
        <KuikaTooltip content="Import rows from CSV / Excel" position="bottom">
          <KuikaButton
            type="tertiary"
            size="sm"
            isIcon
            className="kuika-table-editor__toolbar-action-button"
            data-testid="tableEditorImportButton"
            color="neutral"
            disabled={isLoading}
            onClick={(): void => setIsImportModalOpen(true)}
          >
            <ImportRowsIcon className="kuika-table-editor__toolbar-action-icon" />
          </KuikaButton>
        </KuikaTooltip>
      )}
      {!isReadOnly && (
        <KuikaTooltip content="Export entire table" position="bottom">
          <KuikaButton
            type="tertiary"
            size="sm"
            isIcon
            className="kuika-table-editor__toolbar-action-button"
            color="neutral"
            disabled={isLoading || isExporting}
            onClick={openExportMenu}
          >
            <ExportRowsIcon className="kuika-table-editor__toolbar-action-icon" />
          </KuikaButton>
        </KuikaTooltip>
      )}
      {!isReadOnly && (
        <KuikaTooltip content="Generate rows with AI" position="bottom">
          <KuikaButton
            type="tertiary"
            size="sm"
            isIcon
            className="kuika-table-editor__toolbar-action-button"
            color="neutral"
            disabled={isLoading}
            onClick={(): void => setIsAiModalOpen(true)}
          >
            <GenerateWithAiSvg width={16} height={16} />
          </KuikaButton>
        </KuikaTooltip>
      )}
    </>
  );

  const renderCommit = (): React.ReactNode => (
    <>
      {/* Cancel + Save split-button are ALWAYS rendered (not gated
        on isDirty) so undo / redo doesn't yank them in and out
        of the DOM. Disabled state when !isDirty does the visual
        signalling without the layout thrash. Labels stay
        sentence-case in source; uppercase visual treatment is
        applied via CSS to match the sibling Edit Table panel's
        k_button. */}
      <KuikaButton
        type="text"
        size="sm"
        className="kuika-table-editor__cancel-button"
        disabled={!canDiscard || isSaving}
        onClick={(e: React.MouseEvent<HTMLElement>): void => {
          // Below the threshold, the cost of an accidental discard is
          // low (1-2 edits, recoverable with ⌘Z), so we skip the
          // confirm. Above it, OR if any deletes are pending (the
          // user explicitly chose to remove rows — undoing that
          // intent should require deliberate confirmation), we
          // anchor the popover to the button's bottom edge.
          const needsConfirm: boolean = dirtyCount >= DISCARD_CONFIRM_THRESHOLD || pending.deletes.size > 0;
          if (!needsConfirm) {
            discardAll();
            return;
          }
          const rect: DOMRect = (e.currentTarget as HTMLElement).getBoundingClientRect();
          setPendingDiscard({ anchor: { x: rect.left, y: rect.bottom + 4 } });
        }}
      >
        Discard
      </KuikaButton>
      <KuikaSplitButton
        size="sm"
        className="kuika-table-editor__save-split"
        aria-label="Save actions"
        menuLabel="More save actions"
        data-testid="tableEditorSaveButton"
        toggleTestId="tableEditorSaveMenuTrigger"
        status={saveStatus}
        // Nothing to save and nothing to say: off. While the ✓ (or "Try again") is up the
        // button stays lit so its word is legible; a click then finds nothing dirty and the
        // hook ignores it.
        disabled={!isDirty && saveStatus === "idle"}
        // The alternatives act on the pending edits, so they wait for some.
        menuDisabled={!isDirty}
        onClick={(): void => {
          void saveAll();
        }}
        options={saveMenuOptions}
      >
        {/* Save count rides as a small circular pill instead of
          appended text. The pill reads as state metadata —
          separate from the action verb — and tabular-nums on
          the inner span stops the digits jittering as the
          count ticks up. The count is only mounted when
          dirty, so "Save" alone is the resting label. */}
        <span className="kuika-table-editor__save-button-label">
          <span>Save</span>
          {isDirty && (
            <span
              className="kuika-table-editor__save-button-count"
              data-testid="tableEditorSaveCount"
              // The value as a plain number, for readers of the DOM: the rolling digits'
              // text is not the value while they roll.
              data-count={dirtyCount}
              aria-hidden="true"
            >
              <KuikaNumber value={dirtyCount} />
            </span>
          )}
        </span>
      </KuikaSplitButton>
    </>
  );

  // The table glyph every data-diagram bar shows, in the database family's colour.
  const tableGlyph: IEditorGlyph = getEditorGlyph({ kind: "table", datasourceName });

  return (
    <div className={embedded ? "kuika-table-editor kuika-table-editor--embedded" : "kuika-table-editor"}>
      {/* Screen-reader announcement for what the Save button's own live
          status leaves out: the count being saved and why a save failed.
          aria-live="polite" lets the in-progress edit finish before the
          announcement fires. The string is derived per render from the
          mutations hook's state. */}
      <div role="status" aria-live="polite" className="kuika-table-editor__sr-only">
        {liveSaveAnnouncement}
      </div>
      {embedded ? (
        // The table editor's bar above already names the table and is the way out: no back, no
        // switcher, no name — the counts and filters on the left, the tools and the commit on the right.
        <div className="kuika-table-editor__subbar">
          <span className="kuika-table-editor__subbar-start">
            {renderMeta()}
            {renderStatus()}
          </span>
          <span className="kuika-table-editor__subbar-end">
            <span className="kuika-table-editor__toolbar-right" data-testid="tableEditorToolbar">
              {renderTools()}
            </span>
            {renderCommit()}
          </span>
        </div>
      ) : (
        <KuikaPageHeader
          variant="bar"
          className="kuika-table-editor__toolbar"
          title={toolbarTitle}
          onBack={
            onClose
              ? (): void => {
                  // No event here, so the guard is anchored on the control itself.
                  const back: Element | null = document.querySelector(".kuika-table-editor__toolbar .kuika-button");
                  const box: DOMRect | undefined = back?.getBoundingClientRect();
                  guardDestructive(box?.left ?? 0, box?.bottom ?? 0, onClose);
                }
              : undefined
          }
          backLabel="Back to Datasources"
          renderTitle={(): React.ReactNode => (
            <span className="kuika-table-editor__toolbar-left">
              {switcherItems && onSwitchTable ? (
                <KuikaEntitySwitcher
                  trigger="chip"
                  triggerIcon={<EditorGlyphIcon glyph={tableGlyph} />}
                  triggerIconTooltip={tableGlyph.label}
                  triggerLabel={toolbarTitle}
                  triggerTestId="tableRowsSwitcherTrigger"
                  items={switcherItems}
                  current={switcherItems.find((item: ITableRowsSwitcherItem): boolean => item.name === toolbarTitle)}
                  entityLabel="Table"
                  iconName="table"
                  // The list the footer leads back to is the datasources one — the same way out
                  // as the back control, through the same discard guard.
                  onManageAll={
                    onClose
                      ? (): void => {
                          const back: Element | null = document.querySelector(
                            ".kuika-table-editor__toolbar .kuika-button"
                          );
                          const box: DOMRect | undefined = back?.getBoundingClientRect();
                          guardDestructive(box?.left ?? 0, box?.bottom ?? 0, onClose);
                        }
                      : undefined
                  }
                  onSelect={(item: KuikaEntitySwitcherItem, triggerRect: DOMRect): void => {
                    guardDestructive(triggerRect.left, triggerRect.bottom, (): void =>
                      onSwitchTable(item as ITableRowsSwitcherItem)
                    );
                  }}
                />
              ) : (
                <KuikaEditorIdentity
                  icon={<EditorGlyphIcon glyph={tableGlyph} />}
                  iconTooltip={tableGlyph.label}
                  name={toolbarTitle}
                />
              )}
              {renderMeta()}
            </span>
          )}
          actions={
            <span className="kuika-table-editor__toolbar-right" data-testid="tableEditorToolbar">
              {renderStatus()}
              {renderTools()}
              {renderCommit()}
            </span>
          }
        />
      )}

      {embedded?.notice}
      {isReadOnly && columns.length > 0 && (
        <div className="kuika-table-editor__banner kuika-table-editor__banner--info">
          {`System table — view only. You can read rows here, but edits, imports, and exports are disabled.${sensitiveHiddenColumns.length > 0 ? " Some sensitive columns are hidden." : ""}`}
        </div>
      )}
      {noPrimaryKey && (
        <div className="kuika-table-editor__banner kuika-table-editor__banner--info">
          {`This table has no primary key, so inline edits can't be saved. Add a primary key ${embedded ? "on the Columns tab" : "on the table designer"} to turn editing on.`}
        </div>
      )}
      {loadError && (
        <TableEditorAlert
          alert={loadError}
          actions={loadErrorActions}
          testId="tableEditorLoadAlert"
          onDismiss={(): void => setLoadError(null)}
        />
      )}
      {saveError && (
        <TableEditorAlert
          alert={saveError}
          actions={saveErrorActions}
          testId="tableEditorSaveAlert"
          onDismiss={(): void => setSaveError(null)}
        />
      )}
      {aiBusy && (
        <div className="kuika-table-editor__banner kuika-table-editor__banner--info kuika-table-editor__banner--busy">
          <span className="kuika-table-editor__banner-spinner" aria-hidden="true" />
          <span>{aiBusy.kind === "row" ? "Filling empty cells with AI…" : "Filling the new row with AI…"}</span>
        </div>
      )}
      {!aiBusy && aiNotice && (
        <TableEditorAlert alert={aiNotice} testId="tableEditorNoticeAlert" onDismiss={(): void => setAiNotice(null)} />
      )}

      <div className="kuika-table-editor__grid-wrapper">
        {/* First load: nothing of the table is known yet, so its shape is previewed (after a short
            wait, so a fast answer never flashes a placeholder). A reload with rows on screen keeps
            them where they are and says so beside the row count instead of covering the designer. */}
        {rowsPresentation === "skeleton" && (
          <KuikaSkeletonRegion label="Loading rows" className="kuika-table-editor__grid-skeleton">
            <KuikaSkeletonTable rows={8} header />
          </KuikaSkeletonRegion>
        )}
        {!isGridWaiting &&
          (columns.length === 0 && !isLoading ? (
            <div className="kuika-table-editor__empty">
              <KuikaSvg type="common" name="table" width={32} height={32} color="var(--dark-6)" />
              <span className="title">Can&apos;t load this table</span>
              <span className="desc">
                The server didn&apos;t return any columns. Check that the table still exists and that your role can read
                it.
              </span>
            </div>
          ) : (
            <EditableDataGrid
              columns={columns}
              primaryKeyColumns={primaryKeyColumns}
              foreignKeys={foreignKeys}
              rows={rows}
              pendingChanges={pending}
              saveIssuesByClientId={gridIssuesByClientId}
              isReadOnly={isReadOnly}
              isLoading={isLoading}
              sort={sort}
              onSortChange={onSortChange}
              onEditCell={editCell}
              onEditInsertCell={editInsertCell}
              onAddRow={addInsertRow}
              onRemoveInsert={removeInsertRow}
              onDeleteRow={markRowDeleted}
              onUndeleteRow={unmarkRowDeleted}
              pageStartIndex={(loadedPageNumber - 1) * loadedPageSize}
              selectedPks={selectedPks}
              onRowSelectionClick={handleRowSelectionClick}
              onSelectAllOnPage={handleSelectAllOnPage}
              onRowContextMenu={openContextMenu}
              onInsertRowContextMenu={openInsertContextMenu}
              columnFilters={columnFilters}
              onColumnFilterChange={setColumnFilter}
              highlightedInsertIds={highlightedInsertIds}
              columnWidths={columnWidths}
              hiddenColumns={effectiveHiddenColumns}
              onColumnWidthChange={setColumnWidth}
              onCellEditBlur={endEditSession}
            />
          ))}
      </div>

      <div className="kuika-table-editor__footer">
        <div className="kuika-table-editor__footer-slot kuika-table-editor__footer-slot--start">
          {totalCount > 0 ? (
            <span className="kuika-table-editor__footer-count">
              {/* Both endpoints derived from the loaded pagination so
                  the "showing X-Y" label matches the gutter — clicking
                  Next on a 25-page-size, 32-row table no longer
                  briefly shows "26-50 of 32" before the rows update. */}
              <span className="num">{(loadedPageNumber - 1) * loadedPageSize + 1}</span>
              <span className="dash">–</span>
              <span className="num">{Math.min(loadedPageNumber * loadedPageSize, totalCount)}</span>
              <span className="of">of</span>
              <span className="num">
                <KuikaNumber value={totalCount} />
              </span>
            </span>
          ) : (
            <span className="kuika-table-editor__footer-count kuika-table-editor__footer-count--empty">
              <span className="of">No rows</span>
            </span>
          )}
        </div>
        <div className="kuika-table-editor__footer-slot kuika-table-editor__footer-slot--center" />

        <div className="kuika-table-editor__footer-slot kuika-table-editor__footer-slot--end">
          <div className="kuika-table-editor__paginator">
            <button
              type="button"
              className="kuika-table-editor__paginator-btn"
              disabled={pageNumber <= 1 || isLoading}
              onClick={(): void => setPageNumber(Math.max(1, pageNumber - 1))}
              aria-label="Previous page"
            >
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                <path
                  d="M6.5 1.5 3 5l3.5 3.5"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <span className="kuika-table-editor__paginator-pos">
              <span className="num">{pageNumber}</span>
              <span className="sep">/</span>
              <span className="num">{totalPages}</span>
            </span>
            <button
              type="button"
              className="kuika-table-editor__paginator-btn"
              disabled={pageNumber >= totalPages || isLoading}
              onClick={(): void => setPageNumber(Math.min(totalPages, pageNumber + 1))}
              aria-label="Next page"
            >
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                <path
                  d="m3.5 1.5 3.5 3.5-3.5 3.5"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
          <div className="kuika-table-editor__page-size">
            <KuikaSelectbox
              size="sm"
              value={pageSize}
              options={PAGE_SIZE_OPTIONS}
              onChange={(next: string | number | (string | number)[] | null): void => {
                if (typeof next === "number") {
                  setPageSize(next);
                  setPageNumber(1);
                }
              }}
            />
          </div>
        </div>
      </div>

      {contextMenu && (
        <RowContextMenu x={contextMenu.x} y={contextMenu.y} options={contextMenuOptions} onClose={closeContextMenu} />
      )}

      {exportAnchor && (
        <RowContextMenu
          x={exportAnchor.x}
          y={exportAnchor.y}
          options={exportAllMenuOptions}
          onClose={closeExportMenu}
        />
      )}

      <BufferedExportConfirmModal confirm={bufferedExportConfirm} />

      {columnsMenuAnchor && (
        <RowContextMenu
          x={columnsMenuAnchor.x}
          y={columnsMenuAnchor.y}
          options={[
            ...columns.map((c: IDatabaseColumnModel): IRowContextMenuOption => {
              // Use a leading ✓ to indicate visible, blank otherwise.
              // RowContextMenu doesn't have a native checkbox affordance;
              // the prefix is the cheapest way to read the toggle state
              // at a glance without adding a new menu primitive.
              const hidden: boolean = effectiveHiddenColumns.includes(c.name);
              // Hiding the last visible column would leave the grid
              // with zero body cells and a hidden-state the user can't
              // see (because the toolbar's count is the only signal).
              // Refuse and dim the option to make the rule visible.
              const wouldLeaveZeroVisible: boolean = !hidden && columns.length - effectiveHiddenColumns.length <= 1;
              return {
                type: "item",
                label: `${hidden ? "    " : "✓  "}${c.name}`,
                disabled: wouldLeaveZeroVisible,
                keepOpenOnClick: true,
                onClick: (): void => {
                  toggleColumnHidden(c.name);
                  // keepOpenOnClick (set above) keeps the menu open so
                  // the user can toggle multiple columns without
                  // re-opening — common case is "hide these 5 cols."
                }
              };
            }),
            { type: "separator" },
            {
              type: "item",
              label: "Show all",
              disabled: effectiveHiddenColumns.length === 0,
              onClick: (): void => {
                setHiddenColumns([]);
              }
            },
            {
              type: "item",
              label: "Reset widths",
              disabled: Object.keys(columnWidths).length === 0,
              onClick: (): void => {
                setColumnWidths({});
              }
            }
          ]}
          onClose={(): void => setColumnsMenuAnchor(null)}
        />
      )}

      {pendingDiscard && (
        <DiscardConfirmPopover
          anchor={pendingDiscard.anchor}
          dirtyCount={dirtyCount}
          deleteCount={pending.deletes.size}
          onConfirm={(): void => {
            const proceed: (() => void) | undefined = pendingDiscard.proceed;
            discardAll();
            setPendingDiscard(null);
            // Run the pending action AFTER the discard commits.
            // Close / navigate handlers may unmount this component;
            // calling them last keeps the cleanup ordering safe.
            proceed?.();
          }}
          onCancel={(): void => setPendingDiscard(null)}
        />
      )}

      <ImportRowsModal
        isOpen={isImportModalOpen}
        tableName={tableName}
        tableSchema={tableSchema}
        hasPrimaryKey={primaryKeyColumns.length > 0}
        onClose={(): void => setIsImportModalOpen(false)}
        onImported={handleImportComplete}
      />

      <AiGenerateRowsModal
        isOpen={isAiModalOpen}
        tableName={tableName}
        onClose={(): void => setIsAiModalOpen(false)}
        onSubmit={aiGenerateRows}
      />

      <SqlPreviewModal
        isOpen={previewRequest !== null}
        request={previewRequest}
        isSaving={isSaving}
        onClose={(): void => setPreviewRequest(null)}
        onConfirmSave={(): void => {
          void saveAll();
        }}
      />
    </div>
  );
});

// Memoised for the table editor, which re-renders on every column edit while its Data tab sits
// mounted beneath: the props it passes are stable, so the grid is left alone.
const TableRowsEditor = React.memo(TableRowsEditorComponent);
TableRowsEditor.displayName = "TableRowsEditor";

export { TableRowsEditor };
